import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Atrapa el error en vez de dejar la pantalla en negro.
 *
 * ── Por qué hace falta ──────────────────────────────────────────────────────
 * Sin un límite de error, cualquier excepción en el render desmonta el árbol
 * ENTERO de React y la aplicación queda en negro. Eso ya pasó cuatro veces en
 * este módulo, y cada una costó lo mismo: el usuario reporta «se pone negro»,
 * que es la única información disponible, y hay que reconstruir qué tocó.
 *
 * Vivía en `monitoreo-ia` y sólo protegía sus tres paneles. Las otras 211 rutas
 * —el mapa en vivo, administración, CRM, punto de venta— no tenían ninguno: un
 * `undefined` que llegara raro del socket o del API dejaba en blanco la
 * aplicación entera, no un panel. Por eso ahora es una pieza compartida y se
 * monta también en la raíz.
 *
 * Con esto el fallo queda acotado al panel que lo produjo, y lo más importante:
 * el mensaje del error se ve. La diferencia entre «se pone negro» y
 * «Cannot read properties of undefined (reading 'length')» es la diferencia
 * entre media hora de búsqueda y treinta segundos.
 *
 * ── Por qué NO oculta el problema ───────────────────────────────────────────
 * Un límite de error puede convertirse en una alfombra bajo la que barrer
 * fallos: se traga la excepción, enseña algo bonito y nadie se entera. Por eso
 * esto enseña el mensaje crudo y deja el `console.error` intacto — no está para
 * que el fallo desaparezca, está para que no se lleve la pantalla con él.
 */

interface Props {
  children: ReactNode;
  /** Qué parte es, para que el mensaje diga dónde ocurrió. */
  zona: string;
  /**
   * `panel` para un recuadro dentro de una pantalla; `pagina` cuando lo que
   * falló se lleva la vista completa. Cambia sólo la presentación: a pantalla
   * completa el aviso de panel se ve como una nota perdida en el centro, y
   * hace falta ofrecer recargar porque puede no quedar nada con qué navegar.
   */
  variante?: "panel" | "pagina";
}

interface State {
  error: Error | null;
}

export default class LimiteDeError extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Se conserva el volcado completo: el límite acota el daño visual, no el
    // diagnóstico.
    console.error(`[${this.props.zona}]`, error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.variante === "pagina") {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-app p-6 text-center">
          <i className="ri-bug-line text-3xl text-error" aria-hidden="true" />
          <p className="text-base font-semibold text-content">
            Algo falló en {this.props.zona}
          </p>
          <p className="max-w-md break-words font-mono text-xs leading-relaxed text-content-faint">
            {error.message}
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="rounded-lg border border-outline/40 px-4 py-1.5 text-sm text-content-secondary transition-colors hover:bg-subtle"
            >
              Reintentar
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-lg bg-primary-500 px-4 py-1.5 text-sm text-on-primary transition-colors hover:bg-primary-400"
            >
              Recargar la página
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 bg-card p-4 text-center">
        <i className="ri-bug-line text-xl text-error" aria-hidden="true" />
        <p className="text-xs font-semibold text-content">
          Algo falló en {this.props.zona}
        </p>
        <p className="max-w-xs break-words font-mono text-[10px] leading-relaxed text-content-faint">
          {error.message}
        </p>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          className="mt-1 rounded-lg border border-outline/40 px-3 py-1 text-[11px] text-content-secondary transition-colors hover:bg-subtle"
        >
          Reintentar
        </button>
      </div>
    );
  }
}
